import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const ADDED_CLASS = 'c-pair-card__button--added';

/**
 * Keeps the "Pair it with" cards in sync with the cart.
 *
 * The block renders inside `product-recommendations`, which swaps its own innerHTML once the
 * recommendations resolve. That replaces this element too, so each instance only ever owns the
 * cards it was rendered with and the listeners follow the connect/disconnect lifecycle.
 *
 * @extends {Component}
 */
class PairItWith extends Component {
  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  #onVariantUpdate = () => {
    for (const form of this.querySelectorAll('.c-pair-card__form')) {
      const button = form.querySelector('.c-pair-card__button');
      const variantId = form.querySelector('input[name="id"]');

      if (!(button instanceof HTMLButtonElement) || !(variantId instanceof HTMLInputElement)) continue;

      button.disabled = variantId.disabled || button.classList.contains(ADDED_CLASS);
    }
  };

  #onCartUpdate = async () => {
    const cards = this.querySelectorAll('.c-pair-card[data-pair-virtual-class]');

    if (!cards.length) return;

    /** @type {string[]} */
    let productIds = [];

    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`);

      if (!response.ok) return;

      const cart = await response.json();
      productIds = (cart.items ?? []).map((/** @type {{ product_id: number }} */ item) => String(item.product_id));
    } catch {
      return;
    }

    for (const card of cards) {
      const button = card.querySelector('button.c-pair-card__button');

      if (!(button instanceof HTMLButtonElement)) continue;

      const isAdded =
        card.hasAttribute('data-pair-purchased') ||
        productIds.includes(/** @type {HTMLElement} */ (card).dataset.productId ?? '');
      const variantId = card.querySelector('.c-pair-card__form input[name="id"]');

      button.classList.toggle(ADDED_CLASS, isAdded);
      button.disabled = isAdded || (variantId instanceof HTMLInputElement && variantId.disabled);
    }
  };
}

if (!customElements.get('pair-it-with')) {
  customElements.define('pair-it-with', PairItWith);
}
